import type { EventListQuery } from '@lib/input/event-list.input'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useEventPlanners, useEvents } from '@/hooks/useEvents'
import { useVenues } from '@/hooks/useVenues'
import { useAuth } from '@/auth/AuthContext'
import { canCreateEvents, isStaff } from '@/auth/access'
import { isInVenueSupportMode } from '@/auth/roles'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/badge'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { ListSkeleton, ErrorState } from '@/components/ui/states'
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell, TableEmptyRow } from '@/components/ui/table'
import { TableThumbCell, TableThumbHead } from '@/components/TableThumbCell'
import { Pagination } from '@/components/ui/pagination'
import { serverPage } from '@/lib/pagination'
import { eventPhoto } from '@/lib/eventPhoto'
import { formatEventDate } from '@/lib/eventTime'
import { UnconfirmedPlansAlert } from '@/components/UnconfirmedPlansAlert'
import {
  EventsFilterChips,
  EventsSearch,
  EMPTY_EVENTS_FILTERS,
  eventsFiltersActive,
  type EventsFilterState,
} from '@/components/EventsFilters'
import type { EventDto, EventListDto } from '@lib/dto/event.dto'
import { eventStatusKey } from './eventsList'

function emptyFilterDescription(filters: EventsFilterState): string {
  const q = filters.query.trim()
  if (q !== '') return `Nothing named “${q}”.`
  return 'No events match these filters.'
}

/** Unique venues present in the loaded list, sorted by name. */
/**
 * The venue filter's options.
 *
 * From the venues list rather than from the events on screen, which is what
 * they used to come from: once the API returns one page, deriving them from the
 * rows would offer only the venues that happen to appear on that page — and
 * choosing one would then hide the option that produced it.
 */
function venueOptions(venues: { id: string; name: string }[]): { id: string; name: string }[] {
  return [...venues].sort((a, b) => a.name.localeCompare(b.name))
}



/** The inline button in a table's empty row — text-sized, underlined. */
const INLINE_ACTION =
  'underline underline-offset-2 outline-none transition-colors hover:text-ink focus-visible:focus-ds'

/** Events index: search, then a table of events across venues with its filters in the bar. */
export function EventsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  // The same rule as /events/new: only someone working at a venue.
  const canCreate = canCreateEvents(user)
  const [page, setPage] = React.useState(1)
  const [filters, setFilters] = React.useState<EventsFilterState>(EMPTY_EVENTS_FILTERS)
  // A client whose events are all finished would otherwise land on an empty
  // upcoming list. Open past once, and leave a later choice of upcoming alone.
  const [openedPast, setOpenedPast] = React.useState(false)
  const [openingPast, setOpeningPast] = React.useState(false)

  /** Filtering starts the list again — see the note on `UsersPage.filterTo`. */
  function filterTo(next: EventsFilterState): void {
    setFilters(next)
    setPage(1)
  }
  // The venue this page is scoped to, for anyone working inside one — a venue
  // admin or an organization admin who logged in to it. Not role-gated: the
  // question is "am I in a venue", and an entered organization admin who still
  // saw every venue's events would be reading a list the rest of their session
  // is not pointed at.
  const focusVenueId = isInVenueSupportMode(user) ? user.supportVenue.venueId : null

  function openEvent(id: string): void {
    navigate(`/events/${id}`)
  }

  /**
   * The filters as the API's query.
   *
   * A venue in support mode is focus rather than permission — the API still
   * returns every venue in reach — so it becomes the venue filter, and it wins
   * over the chip, which that mode hides anyway.
   */
  const query: EventListQuery = {
    ...(focusVenueId ?? filters.venueId ? { venueId: focusVenueId ?? filters.venueId ?? undefined } : {}),
    ...(filters.status !== null ? { status: filters.status } : {}),
    ...(filters.dateFrom !== '' ? { from: filters.dateFrom } : {}),
    ...(filters.dateTo !== '' ? { to: filters.dateTo } : {}),
    ...(filters.query.trim() !== '' ? { q: filters.query.trim() } : {}),
    ...(filters.planner !== null ? { planner: filters.planner } : {}),
  }

  const eventsQuery = useEvents(query, page)
  const upcomingIsEmpty =
    user?.role === 'CLIENT' &&
    !openedPast &&
    !eventsFiltersActive(filters) &&
    eventsQuery.isSuccess &&
    !eventsQuery.isPlaceholderData &&
    eventsQuery.data.total === 0
  if (upcomingIsEmpty) {
    setOpenedPast(true)
    setOpeningPast(true)
    setFilters((current) => ({ ...current, status: 'past' }))
  }
  if (openingPast && filters.status === 'past' && eventsQuery.isSuccess && !eventsQuery.isPlaceholderData) {
    setOpeningPast(false)
  }
  // Unpaged on purpose: these are the venue filter's options, and a page of
  // venues would offer only some of them.
  const venuesQuery = useVenues()
  const venueList = (venuesQuery.data?.venues ?? []).map((v) => ({ id: v.id, name: v.name }))
  // Asked only by those the route answers: a client reaches this list too.
  const planners = useEventPlanners(isStaff(user)).data?.planners ?? []
  // A column of dashes says nothing, so it waits for the first planner.
  const showPlanner = planners.length > 0
  const columns = 5 + (focusVenueId === null ? 1 : 0) + (showPlanner ? 1 : 0)
  const filtersActive = eventsFiltersActive(filters)

  /** The band's label — the page title already says these are events. */
  function eventCount(count: number): string {
    return `${String(count)} event${count === 1 ? '' : 's'}`
  }

  /**
   * The action that adds to this table, in the table's own header. Ghost, the
   * way the design system puts a quiet `New` in a panel header.
   */
  function newEventAction(): React.ReactElement {
    return (
      <Button size="sm" onClick={() => navigate('/events/new')}>
        <Plus className="h-4 w-4" /> New event
      </Button>
    )
  }

  function renderLoaded(list: EventListDto) {
    const events = list.events
    const venues = venueOptions(venueList)
    // Filtered, ordered and sliced by the API. `total` is what the filters
    // matched, which is both the band's count and the pager's readout.
    const shown = serverPage(page, list.total, events.length)

    return (
      <>
        <EventsSearch filters={filters} onChange={filterTo} />

        {/*
          The table stays when nothing matched, because its bar holds the
          filters: swapping it for an empty state would take them away at the
          moment somebody needs to widen the search. The API answers with the
          count of what matched, not how many events exist, so even an
          unfiltered "nothing" is not proof of an empty organization — the
          default hides past events, and Status → Past is where the rest are.
        */}
        <Table
          heading={eventCount(list.total)}
          actions={
            <>
              <EventsFilterChips
                filters={filters}
                onChange={filterTo}
                venues={venues}
                showVenueFilter={focusVenueId === null}
                planners={planners}
              />
              {canCreate ? newEventAction() : null}
            </>
          }
          footer={
            list.total > 0 ? (
              <Pagination
                page={shown.page}
                pageCount={shown.pageCount}
                from={shown.from}
                to={shown.to}
                total={shown.total}
                unit="events"
                onPage={setPage}
              />
            ) : undefined
          }
        >
          <TableHeader>
            <TableThumbHead />
            <TableHead>Event</TableHead>
            {/* Inside a venue every row is that venue; the column would
                repeat the sidebar on every line. */}
            {focusVenueId === null ? <TableHead>Venue</TableHead> : null}
            <TableHead>Date</TableHead>
            {showPlanner ? <TableHead>Planner</TableHead> : null}
            <TableHead className="text-right">Guests</TableHead>
            <TableHead>Status</TableHead>
          </TableHeader>
          <TableBody>
            {list.total === 0 ? (
              <TableEmptyRow colSpan={columns}>{renderNothingMatched()}</TableEmptyRow>
            ) : (
              events.map((ev) => (
                <EventRow
                  key={ev.id}
                  event={ev}
                  showVenue={focusVenueId === null}
                  showPlanner={showPlanner}
                  onOpen={() => openEvent(ev.id)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </>
    )
  }

  /**
   * The one row a list with nothing in it shows: what matched, and the way out
   * — widen the filters, or, with none set, look at past events or make one.
   */
  function renderNothingMatched(): React.ReactElement {
    if (filtersActive) {
      return (
        <>
          {emptyFilterDescription(filters)}{' '}
          <button type="button" onClick={() => filterTo(EMPTY_EVENTS_FILTERS)} className={INLINE_ACTION}>
            Clear filters
          </button>
        </>
      )
    }
    return (
      <>
        {focusVenueId ? 'Nothing upcoming at this venue.' : 'No upcoming events.'}{' '}
        <button type="button" onClick={() => filterTo({ ...filters, status: 'past' })} className={INLINE_ACTION}>
          Show past events
        </button>
      </>
    )
  }

  function renderContent() {
    if (eventsQuery.isPending || openingPast) {
      return <ListSkeleton rows={6} />
    }
    if (eventsQuery.isError) {
      return <ErrorState message={requestErrorMessage(eventsQuery.error)} onRetry={() => void eventsQuery.refetch()} />
    }
    return renderLoaded(eventsQuery.data)
  }

  return (
    <PageContainer width="wide" className="space-y-5">
      <PageHeader title="Events" description="Every event across your venues." />

      {renderContent()}
    </PageContainer>
  )
}

/** One event row — the whole row navigates to the event detail. */
function EventRow({
  event,
  showVenue,
  showPlanner,
  onOpen,
}: {
  event: EventDto
  /** False inside a venue, where the column is dropped from the header too. */
  showVenue: boolean
  showPlanner: boolean
  onOpen: () => void
}) {
  return (
    <TableRow
      className="cursor-pointer"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      tabIndex={0}
    >
      {/* Every row has a picture, and a row without one of its own gets the
          default photograph rather than a gap — the column is a column, not a
          scatter of frames. The old inline thumbnail appeared only when a photo
          existed, which read as rows missing something. */}
      {/* The venue's photograph when the event has none of its own — the same
          choice the event's own page makes, so the row and the page it opens
          show the same building. */}
      <TableThumbCell {...eventPhoto(event)} />
      <TableCell className="font-medium">
        <span className="flex items-center gap-2.5">
          <span className="min-w-0 truncate">{event.name}</span>
          <UnconfirmedPlansAlert
            unconfirmed={event.unconfirmedPhaseCount}
            total={event.phaseCount}
          />
        </span>
      </TableCell>
      {showVenue ? <TableCell className="text-ink-muted">{event.venueName}</TableCell> : null}
      <TableCell className="tabular-nums text-ink-muted">{formatEventDate(event.date)}</TableCell>
      {showPlanner ? (
        <TableCell className="text-ink-muted">{event.planner?.companyName ?? '—'}</TableCell>
      ) : null}
      <TableCell className="text-right tabular-nums text-ink-muted">{event.guestCount}</TableCell>
      <TableCell>
        <StatusBadge status={eventStatusKey(event)} />
      </TableCell>
    </TableRow>
  )
}
